import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production'
    ? '/api'
    : 'http://localhost:5000/api'
);

export const AUTH_TOKEN_UPDATED_EVENT = 'auth:token-updated';

const AUTH_REFRESH_TIMEOUT_MS = 15000;

const api = axios.create({ baseURL: '/api', withCredentials: true });

let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(p => {
    if (error) {
      p.reject(error);
    } else if (typeof token === 'string' && token.length > 0) {
      p.resolve(token);
    } else {
      p.reject(new Error('Token refresh queue: missing token'));
    }
  });
  failedQueue = [];
};

const isAuthEndpointRequest = (config: { url?: string }) =>
  Boolean(
    config.url &&
    (config.url.includes('/auth/refresh') || config.url.includes('/auth/login'))
  );

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  async error => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const shouldTryRefresh =
      status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpointRequest(originalRequest);

    if (!shouldTryRefresh) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true, timeout: AUTH_REFRESH_TIMEOUT_MS }
      );
      const newToken =
        typeof data?.token === 'string' ? data.token.trim() : '';
      if (!newToken) {
        throw new Error('Invalid refresh response: missing token');
      }
      localStorage.setItem('token', newToken);
      window.dispatchEvent(
        new CustomEvent(AUTH_TOKEN_UPDATED_EVENT, { detail: { token: newToken } })
      );
      processQueue(null, newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (err) {
      processQueue(err, null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.replace('/');
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;

export { API_BASE_URL };

export const createClientProfile = async (clientData: any) => {
  const response = await api.post(`${API_BASE_URL}/clients`, clientData);
  return response.data;
};

export const getClientProfiles = async () => {
  const response = await api.get(`${API_BASE_URL}/clients`);
  return response.data;
};

export const createUser = async (clientId: string, userData: any) => {
  const response = await api.post(`${API_BASE_URL}/clients/${clientId}/users`, userData);
  return response.data;
};

export const getUsersByClient = async (clientId: string) => {
  const response = await api.get(`${API_BASE_URL}/clients/${clientId}/users`);
  return response.data;
};

export const uploadImage = async (imageData: File) => {
  const formData = new FormData();
  formData.append('image', imageData);
  const response = await api.post(`${API_BASE_URL}/images`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const trackUsage = async (serviceType: string, usageId?: number) => {
  const token = localStorage.getItem('token');
  const response = await api.post(`${API_BASE_URL}/usage/track`, {
    service_type: serviceType,
    usage_id: usageId
  }, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  return response.data;
};

export const getUsageStatistics = async () => {
  const token = localStorage.getItem('token');
  const response = await api.get(`${API_BASE_URL}/usage`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  return response.data;
};

export const getUsers = async () => {
  const response = await api.get(`${API_BASE_URL}/all-users`);
  return response.data;
};

export const createAnyUser = async (userData: any) => {
  const response = await api.post(`${API_BASE_URL}/all-users`, userData);
  return response.data;
};

export const deleteAnyUser = async (userId: string, role: string) => {
  const response = await api.delete(`${API_BASE_URL}/all-users/${userId}?role=${role}`);
  return response.data;
};

export const updateUserBillingAmount = async (clientId: number, billingAmount: number) => {
  const response = await api.put(`${API_BASE_URL}/users/billing/${clientId}`, { billing_amount: billingAmount });
  return response.data;
};

export const deleteUser = async (userId: string) => {
  const response = await api.delete(`${API_BASE_URL}/users/${userId}`);
  return response.data;
};

export const fetchUsageData = async () => {
  const token = localStorage.getItem('token');
  const response = await api.get(`${API_BASE_URL}/usage`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  return response.data;
};

export const getAnalytics = async (period: string = '30days') => {
  const token = localStorage.getItem('token');
  const response = await api.get(`${API_BASE_URL}/usage/analytics?period=${period}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  return response.data;
};

export const getClientProfile = async (clientId: string) => {
  const response = await api.get(`${API_BASE_URL}/clients/${clientId}`);
  return response.data;
};

export const updateClientProfile = async (clientId: string, clientData: any) => {
  const response = await api.put(`${API_BASE_URL}/clients/${clientId}`, clientData);
  return response.data;
};

export const fetchClientData = async () => {
  const response = await api.get(`${API_BASE_URL}/clients`);
  return response.data;
};

export const login = async (credentials: any) => {
  const response = await api.post(`${API_BASE_URL}/auth/login`, credentials);
  return response.data;
};

export const changePassword = async (currentPassword: string, newPassword: string) => {
  const token = localStorage.getItem('token');
  const response = await api.post(`${API_BASE_URL}/auth/change-password`, {
    currentPassword,
    newPassword
  }, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  return response.data;
};